"use client";

import * as stylex from "@stylexjs/stylex";
import { ChevronLeft, ChevronRight, Minimize, Pause, Play } from "lucide-react";
import { useEffect, useRef } from "react";
import { copy } from "@/copy";
import type { Photo } from "@/domain/schemas";
import "@/theme/tokens.stylex";
import { colors, sizes, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { IconButton } from "@/ui/IconButton";
import { photoMeta } from "../slideshow";
type FullScreenPhotoProps = {
  photo: Photo;
  src: string;
  index: number;
  total: number;
  thisYear: number;
  playing: boolean;
  chrome: boolean;
  onExit: () => void;
  onToggleChrome: () => void;
  onStep: (delta: number) => void;
  onTogglePlay: () => void;
};
type KeyActions = Pick<FullScreenPhotoProps, "onExit" | "onStep" | "onTogglePlay">;
export function FullScreenPhoto({
  photo,
  src,
  index,
  total,
  thisYear,
  playing,
  chrome,
  onExit,
  onToggleChrome,
  onStep,
  onTogglePlay
}: FullScreenPhotoProps) {
  const dialog = useFocusOnOpen();
  useKeys({
    onExit,
    onStep,
    onTogglePlay
  });
  return <div ref={dialog} role="dialog" aria-modal="true" aria-label={copy.photos.fullScreenName(photo.title)} tabIndex={-1} className="xixxii4 x10a8y8t x1355qak x1i4uan3 x1btspmr x78zum5 xdt5ytf x1a2a7pz">
      {chrome && <div className="x2lah0s x78zum5 x1qughib x6s0dn4 xddi9bj xf0hean x9f619 xefclja">
          <span className="xr0iwhn xif65rj xwj1eqv xtvhhri xuxw1ft">{copy.photos.position(index + 1, total)}</span>
          <IconButton icon={Minimize} label={copy.photos.exitFullScreen} variant="inverse" size="touch" onClick={onExit} />
        </div>}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: tapping the photo only hides or shows the rows; the keyboard has Esc, the arrows and Space */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the keys are handled on the window for the whole dialog */}
      <div onClick={onToggleChrome} className="x1iyjqo2 x2lwn1j x1n2onr6 x1ypdohk">
        {/* biome-ignore lint/performance/noImgElement: photos come pre-sized from the authenticated file route and the service worker cache, not the Next image optimizer */}
        <img src={src} alt={photo.title} className="x10l6tqk x10a8y8t xh8yej3 x5yr21d x19kjcj4 x1lliihq" />
      </div>
      {chrome && <div className="x2lah0s x78zum5 x1a02dak x1qughib x6s0dn4 xn95wz0 xho1ii4 x9f619">
          <div className="x78zum5 xdt5ytf x17d4w8g xeuugli">
            <span className="xgftyg8 x1v94iar x1roq8pg x1uo3zyz">{photo.title}</span>
            <span className="xr0iwhn xif65rj xky7s93 xtvhhri">{photoMeta(photo, thisYear)}</span>
          </div>
          <div className="x78zum5 x152posx">
            <IconButton icon={ChevronLeft} label={copy.photos.previous} variant="inverse" size="touch" onClick={() => onStep(-1)} />
            <IconButton icon={playing ? Pause : Play} label={playing ? copy.photos.pause : copy.photos.play} variant="inverse" size="touch" onClick={onTogglePlay} />
            <IconButton icon={ChevronRight} label={copy.photos.next} variant="inverse" size="touch" onClick={() => onStep(1)} />
          </div>
        </div>}
    </div>;
}
function useKeys(actions: KeyActions) {
  const latest = useRef(actions);
  useEffect(() => {
    latest.current = actions;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const {
        onExit,
        onStep,
        onTogglePlay
      } = latest.current;
      if (event.key === "Escape") return onExit();
      if (event.key === "ArrowLeft") return onStep(-1);
      if (event.key === "ArrowRight") return onStep(1);
      if (event.key !== " ") return;
      event.preventDefault();
      onTogglePlay();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
function useFocusOnOpen() {
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement;
    dialog.current?.focus();
    return () => {
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);
  return dialog;
}